/**
 * Local-calendar arithmetic for streaks and challenge windows.
 *
 * Everything is in the player's **local** time, never UTC. A daily challenge
 * that rolled over at 3am in Lima or 1am in Wakefield would read as broken,
 * and the player's day is the only day that means anything to them.
 *
 * Built with the `Date(y, m, d)` constructor rather than by adding multiples
 * of 86 400 000 ms, because the latter is wrong twice a year wherever clocks
 * change: a "next midnight" computed across a DST boundary lands at 23:00 or
 * 01:00.
 */

const pad = (n: number) => String(n).padStart(2, '0');

/** `2026-09-16` in local time. */
export function dayKey(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Epoch ms of the local midnight that starts the day containing `ms`. */
export function startOfDay(ms: number): number {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Epoch ms of the next local midnight. */
export function startOfNextDay(ms: number): number {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime();
}

/**
 * Epoch ms of the local Monday midnight that starts the week containing `ms`.
 *
 * Monday by default, because ISO 8601 and the UK both start there. A
 * deployment whose week starts on Sunday or Saturday passes a different
 * `weekStartsOn`; this game's is `WEEK_STARTS_ON` in its content file.
 */
export function startOfWeek(ms: number, weekStartsOn = 1): number {
  const d = new Date(ms);
  const back = (d.getDay() - weekStartsOn + 7) % 7;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - back).getTime();
}

export function startOfNextWeek(ms: number, weekStartsOn = 1): number {
  const d = new Date(startOfWeek(ms, weekStartsOn));
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 7).getTime();
}
